"use client";

/**
 * Global UX-quality signal listeners.
 *
 * Mounted once at the app root via the `<UxSignals />` component
 * (`shared/ui/UxSignals.tsx`). Tracks:
 *   - scroll-depth percentile milestones (25/50/75/100) per pageview
 *   - rage clicks (3+ pointer-downs on the same target inside 1s)
 *   - dead clicks (pointer-down on visibly non-interactive nodes)
 *   - tab visibility transitions (visible↔hidden) with duration
 *
 * NOT CONSENT-GATED, and the header used to claim it was.
 *
 * `track()` sends to PostHog before any consent check, deliberately — a
 * documented owner decision, the same one that applies to Microsoft Clarity
 * (see app/layout.tsx). `persistAnalyticsEvent` gates on an allowlist and on
 * having a submission id, not on consent either. So nothing here exits when
 * analytics consent is missing, and a compliance review that read the old
 * sentence would have stopped at it.
 *
 * Safety rails:
 *   - Scroll uses requestAnimationFrame throttling.
 *   - All pointer/scroll/touch listeners are `passive: true`.
 *   - One-time install guard on a window flag — survives React StrictMode's
 *     double-effect.
 *   - State resets on `pageshow` (iOS BFCache) and `popstate` (App Router
 *     navigations bubble through both `pageshow` for full reloads and
 *     pathname-change from the React side).
 *
 * State per pageview lives on `window.__loveiqUxSignalsState` so consumers
 * can reset it from the React layer when the pathname changes.
 */

import { withoutTrackingParams } from "@shared/url/utm";
import { isGuidedTourActive } from "@shared/ui/guided-tour-state";
import {
  trackDeadClick,
  trackRageClick,
  trackScrollDepth,
  trackTabHidden,
  trackTabVisible,
} from "@features/analytics/client";

declare global {
  interface Window {
    __loveiqUxSignalsInstalled?: boolean;
    __loveiqUxSignalsState?: {
      pathname: string;
      scrollFired: Set<25 | 50 | 75 | 100>;
      deadClickCounts: Map<string, number>;
      tabVisibleSince: number;
      tabHiddenSince: number;
      maxScrollPct: number;
    };
  }
}

const SCROLL_BUCKETS: ReadonlyArray<25 | 50 | 75 | 100> = [25, 50, 75, 100];
const RAGE_WINDOW_MS = 1000;
const RAGE_THRESHOLD = 3;
/** A second dead-click event fires here, carrying the count. See the call site. */
const DEAD_CLICK_REPEAT_THRESHOLD = 3;
// `label` without `for=` still routes its click to the wrapped input, so
// treat ALL labels as interactive to avoid false-positive dead clicks.
const INTERACTIVE_SELECTOR =
  "a, button, [role=button], [tabindex]:not([tabindex='-1']), input, select, textarea, label, summary, [contenteditable='true'], [onclick]";
const SELECTOR_MAX_LEN = 120;

/** The page, not the ad click that brought them — see `withoutTrackingParams`. */
function getPathname(): string {
  if (typeof location === "undefined") return "/";
  return withoutTrackingParams(location.pathname + location.search);
}

function freshState(): NonNullable<Window["__loveiqUxSignalsState"]> {
  const now = Date.now();
  const startsHidden = typeof document !== "undefined" && document.visibilityState === "hidden";
  return {
    pathname: getPathname(),
    scrollFired: new Set(),
    deadClickCounts: new Map(),
    // Page can mount in a background tab. Track the side that's actually
    // running so the first visibility transition reports the correct delta.
    tabVisibleSince: startsHidden ? 0 : now,
    tabHiddenSince: startsHidden ? now : 0,
    maxScrollPct: 0,
  };
}

function ensureState(): NonNullable<Window["__loveiqUxSignalsState"]> {
  if (!window.__loveiqUxSignalsState) {
    window.__loveiqUxSignalsState = freshState();
  }
  return window.__loveiqUxSignalsState;
}

/**
 * A class or id as CSS will read it.
 *
 * Tailwind class names are not CSS identifiers: `sm:hidden`, `text-[14px]` and
 * `mt-3.5` all make `querySelectorAll` throw. 471 of 13,133 dead and rage taps
 * in the 30 days to 2026-09-23 carried one, 16 of them on a real button — the
 * tap the dead-control check is sent to test first — and it could not parse
 * the selector, so it answered "could not measure" about a control that may
 * well have been dead. Every browser this runs in has `CSS.escape`; the
 * fallback exists for jsdom, which has no `CSS` at all.
 */
const cssIdent = (value: string): string =>
  typeof CSS !== "undefined" && typeof CSS.escape === "function" ? CSS.escape(value) : value;

/**
 * Build a compact, anonymized CSS selector for a target. Walks up to the
 * nearest meaningful ancestor (button / link / role) and joins tag + id +
 * one class. Caps at 120 chars to avoid bloating analytics_event metadata.
 */
function selectorFor(target: EventTarget | null): string {
  if (!(target instanceof Element)) return "unknown";
  const anchor = target.closest("[data-track-id], button, a, [role=button]") ?? target;
  const dataId = anchor.getAttribute?.("data-track-id");
  if (dataId) return `[data-track-id=${dataId}]`.slice(0, SELECTOR_MAX_LEN);

  const parts: string[] = [];
  parts.push(anchor.tagName.toLowerCase());
  if (anchor.id) parts.push(`#${cssIdent(anchor.id)}`);
  const className =
    typeof anchor.className === "string" && anchor.className
      ? anchor.className.split(/\s+/)[0]
      : "";
  if (className) parts.push(`.${cssIdent(className)}`);
  return parts.join("").slice(0, SELECTOR_MAX_LEN) || "unknown";
}

/**
 * The locked paywall surface a tap landed in, if any — from `data-paywall-locked`
 * on the surface. The selector alone cannot say: a locked archetype row and an
 * unlocked one share every class, and a tap on the name inside either is just
 * `h3.font-serif`. 64 taps on those rows in 30 days could not be told apart.
 */
function paywallLockedAt(node: Element | null): string | undefined {
  return node?.closest?.("[data-paywall-locked]")?.getAttribute("data-paywall-locked") ?? undefined;
}

/** Only when there is one: an absent key, never `paywall_locked: undefined`. */
function lockedField(node: Element | null): { paywall_locked?: string } {
  const locked = paywallLockedAt(node);
  return locked ? { paywall_locked: locked } : {};
}

/**
 * Why a tap counted as dead, kept because the two are not the same finding.
 *
 * `disabled_control` is the high-signal case: something that looks live, a
 * reader taps it, nothing happens. `non_interactive` is a tap on prose or a
 * container, which is overwhelmingly just reading — 95% of 11,662 events in 30
 * days, against 5% for disabled controls.
 *
 * The distinction was computed and thrown away: the event carried only
 * `pathname` and `target_selector`, so nothing downstream could tell a defect
 * from someone resting a thumb on a paragraph. Answering "how many of these are
 * real?" needed a regex over the selector, which is a guess — the first attempt
 * matched `article` and `aside` because the pattern began `^(button|a|…)`.
 */
export type DeadClickReason = "disabled_control" | "non_interactive";

/**
 * The control a finger was over, when the browser refused to dispatch to it.
 *
 * A DISABLED control carries `pointer-events: none`, so it is not in the event
 * path at all and `event.target` is whatever sits behind it. On the survey that
 * is `nav.flex`, the container — so the single most dead-clicked control on the
 * site was classified as a tap on prose, and `selectorFor` reported the
 * container to the one probe that reads what the scanner claimed.
 *
 * Measured 2026-09-21: PostHog's own `$dead_click` sees "Next" die in 929 of
 * ~1000 sessions, 20,081 taps. Ours recorded 28, none of them as a control.
 *
 * So when nothing interactive is in the path, look for a disabled control whose
 * box contains the point. Verified on production: finds the disabled Next,
 * finds nothing under a live Previous, a heading, or an answer option.
 *
 * Scoped to the hit element's own subtree and only reached when the cheap path
 * already failed, so it costs nothing on an ordinary tap.
 */
function blockedControlAt(hit: Element, x: number, y: number): Element | null {
  for (const el of hit.querySelectorAll(INTERACTIVE_SELECTOR)) {
    const disabled =
      (el as HTMLButtonElement).disabled === true || el.getAttribute("aria-disabled") === "true";
    if (!disabled) continue;
    const b = el.getBoundingClientRect();
    if (x >= b.left && x <= b.right && y >= b.top && y <= b.bottom) return el;
  }
  return null;
}

/**
 * `null` when the tap was on something that genuinely responds.
 *
 * Returns the element to REPORT as well as the reason, because the two differ
 * exactly when it matters: the browser hands us the container and the thing
 * that died is the button inside it.
 */
function deadClickReason(
  target: EventTarget | null,
  x: number,
  y: number
): { reason: DeadClickReason; element: Element } | null {
  if (!(target instanceof Element)) return null;
  const control = target.closest(INTERACTIVE_SELECTOR);
  if (control) {
    /**
     * A DISABLED control is the highest-signal dead click there is: it looks
     * live, a reader taps it, and nothing happens. This listener used to class
     * it as interactive and throw it away, so the only dead clicks we ever
     * recorded were taps on decoration — `div.flex`, `p.font-sans`,
     * `h2.font-serif`. Measured on production: readers tapped a disabled survey
     * "Next" 1,347 times and not one reached this table.
     *
     * `disabled` covers the real form controls; `aria-disabled` covers the
     * custom ones, which stay focusable and so never set the property.
     */
    const isDisabled =
      (control as HTMLButtonElement).disabled === true ||
      control.getAttribute("aria-disabled") === "true";
    // Reported rather than falling through to the cursor walk, which would call
    // it interactive again off any `cursor: pointer` ancestor.
    return isDisabled ? { reason: "disabled_control", element: control } : null;
  }
  // Nothing interactive in the path. Before calling it decoration, check whether
  // a disabled control was under the finger and simply unreachable.
  const blocked = blockedControlAt(target, x, y);
  if (blocked) return { reason: "disabled_control", element: blocked };

  // Walk up checking computed cursor — covers `cursor: pointer` on custom
  // overlays without an explicit role. Only check 3 levels to keep it cheap.
  let node: Element | null = target;
  for (let i = 0; node && i < 3; i++, node = node.parentElement) {
    const style = window.getComputedStyle(node);
    if (style.cursor === "pointer") return null;
  }
  return { reason: "non_interactive", element: target };
}

/**
 * Reset all per-pageview state. Call from React when pathname changes via
 * `usePathname()` — keeps scroll-depth + dead-click dedupe accurate across
 * client-side navigations. Public so the UxSignals component can invoke it.
 */
export function resetUxSignalsForPageview(): void {
  if (typeof window === "undefined") return;
  window.__loveiqUxSignalsState = freshState();
}

export function installUxSignals(): void {
  if (typeof window === "undefined" || typeof document === "undefined") return;
  if (window.__loveiqUxSignalsInstalled) return;
  window.__loveiqUxSignalsInstalled = true;

  ensureState();

  // ── Scroll-depth ────────────────────────────────────────────────────────
  let scrollRafScheduled = false;
  const onScroll = () => {
    if (scrollRafScheduled) return;
    scrollRafScheduled = true;
    requestAnimationFrame(() => {
      scrollRafScheduled = false;
      // The report's guided tour scrolls the page itself; the depth is the reader's own.
      if (isGuidedTourActive()) return;
      const state = ensureState();
      const doc = document.documentElement;
      const total = Math.max(1, doc.scrollHeight - window.innerHeight);
      const pct = Math.min(100, Math.max(0, Math.round((window.scrollY / total) * 100)));
      if (pct <= state.maxScrollPct) return;
      state.maxScrollPct = pct;
      for (const bucket of SCROLL_BUCKETS) {
        if (state.maxScrollPct >= bucket && !state.scrollFired.has(bucket)) {
          state.scrollFired.add(bucket);
          trackScrollDepth(bucket, {
            pathname: state.pathname,
            max_scroll_pct: state.maxScrollPct,
          });
        }
      }
    });
  };
  window.addEventListener("scroll", onScroll, { passive: true });

  // ── Click signals (rage + dead) ─────────────────────────────────────────
  const clickTimestampsByNode = new WeakMap<Element, number[]>();
  const onPointerDown = (event: PointerEvent) => {
    const target = event.target;
    if (!(target instanceof Element)) return;
    // While the report's guided tour runs, the page under its dim is meant not to answer:
    // a tap there is no defect, and the control beneath it would be named as one. Its
    // bubble (a dialog) is watched as ever.
    if (isGuidedTourActive() && !target.closest('[role="dialog"]')) return;
    const state = ensureState();

    /**
     * Rage detection: per-target click timestamps within a 1s window.
     *
     * Resolved through the same blocked-control lookup as the dead click below.
     * A disabled control is `pointer-events: none`, so `event.target` is its
     * container and `closest(...)` finds nothing — rage-tapping a dead "Next"
     * was keyed to `nav.flex` and reported as rage on a layout div. Frustrated
     * repeat taps on a control that cannot respond is the single most useful
     * thing this listener can report, and it was naming the wrong element.
     */
    const blockedForRage =
      target.closest(INTERACTIVE_SELECTOR) === null
        ? blockedControlAt(target, event.clientX, event.clientY)
        : null;
    const node = (blockedForRage ??
      target.closest("[data-track-id], button, a, [role=button]") ??
      target) as Element;
    const now = performance.now();
    const stamps = clickTimestampsByNode.get(node) ?? [];
    const recent = stamps.filter((t) => now - t < RAGE_WINDOW_MS);
    recent.push(now);
    clickTimestampsByNode.set(node, recent);
    if (recent.length === RAGE_THRESHOLD) {
      // Fire exactly once per burst — when we cross the threshold. Further
      // clicks in the same burst won't re-fire because length > threshold
      // skips the equality check.
      trackRageClick({
        pathname: state.pathname,
        // `node`, not `target` — the thing they were hammering, which for a
        // disabled control is not what the browser handed us.
        target_selector: selectorFor(node),
        click_count: recent.length,
        window_ms: RAGE_WINDOW_MS,
        ...lockedField(node),
      });
    }

    // Dead-click detection: pointer-down on something that does not respond.
    const dead = deadClickReason(target, event.clientX, event.clientY);
    if (dead) {
      // The ELEMENT THAT DIED, not the one the browser happened to hand us. A
      // disabled control is `pointer-events: none`, so the target is its
      // container — and this selector is what reaches
      // verify-dead-click-target.mjs, the one probe that checks what the reader
      // actually tapped. Given the container it inspects the wrong thing and
      // correctly reports "ordinary content, not a control": a false clear.
      const selector = selectorFor(dead.element);
      const seen = (state.deadClickCounts.get(selector) ?? 0) + 1;
      state.deadClickCounts.set(selector, seen);
      /**
       * Once on the first tap, once more on the third.
       *
       * Firing once per (pageview, selector) kept volume sane and made a
       * persistent dead tap indistinguishable from an incidental one — and the
       * survey never changes pathname, so one event covered the whole sitting.
       * PostHog counted 20,081 taps on the disabled Next where we recorded 28.
       * Repetition is the thing that separates a defect from a thumb resting on
       * a paragraph, so it has to survive the dedupe. Two events per selector
       * per pageview is still a hard bound.
       */
      if (seen === 1 || seen === DEAD_CLICK_REPEAT_THRESHOLD) {
        trackDeadClick({
          pathname: state.pathname,
          target_selector: selector,
          reason: dead.reason,
          repeat_count: seen,
          ...lockedField(dead.element),
        });
      }
    }
  };
  document.addEventListener("pointerdown", onPointerDown, { passive: true });

  // ── Tab visibility ──────────────────────────────────────────────────────
  const onVisibilityChange = () => {
    const state = ensureState();
    const now = Date.now();
    if (document.visibilityState === "hidden") {
      if (state.tabVisibleSince > 0) {
        trackTabHidden({
          pathname: state.pathname,
          visible_ms: Math.max(0, now - state.tabVisibleSince),
        });
      }
      state.tabHiddenSince = now;
      state.tabVisibleSince = 0;
    } else if (document.visibilityState === "visible") {
      if (state.tabHiddenSince > 0) {
        trackTabVisible({
          pathname: state.pathname,
          hidden_ms: Math.max(0, now - state.tabHiddenSince),
        });
      }
      state.tabVisibleSince = now;
      state.tabHiddenSince = 0;
    }
  };
  document.addEventListener("visibilitychange", onVisibilityChange);

  // ── BFCache safety (iOS Safari pull-to-refresh, etc.) ───────────────────
  // pageshow with persisted=true fires when the tab is restored from BFCache;
  // any per-pageview state should be reset so we don't suppress real signals.
  const onPageShow = (event: PageTransitionEvent) => {
    if (event.persisted) {
      resetUxSignalsForPageview();
    }
  };
  window.addEventListener("pageshow", onPageShow);
}
